/**
 * FileInputButton component for WhatsApp media selection
 * Provides a button to select files with WhatsApp media type filtering and validation
 */

import React, { useRef } from 'react';
import {
  PiPaperclip,
  PiImage,
  PiVideo,
  PiFileAudio,
  PiFile,
} from 'react-icons/pi';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import {
  type ComponentSize,
  type ComponentVariant,
  type ComponentAppearance,
} from 'src/modules/shared/types/component.type';
import {
  WhatsAppMediaType,
  getFileInputAccept,
  validateWhatsAppMediaFile,
  createValidationErrorMessage,
} from '../../../utils/whatsapp/media.util';

const mediaPresentation = {
  [WhatsAppMediaType.IMAGE]: {
    label: 'Add Image',
    tooltip: 'Selecciona una imagen (JPEG, PNG, WebP)',
    Icon: PiImage,
  },
  [WhatsAppMediaType.VIDEO]: {
    label: 'Add Video',
    tooltip: 'Selecciona un video (MP4, 3GPP)',
    Icon: PiVideo,
  },
  [WhatsAppMediaType.AUDIO]: {
    label: 'Add Audio',
    tooltip: 'Selecciona un audio (AAC, MP4, MPEG, AMR, OGG)',
    Icon: PiFileAudio,
  },
  [WhatsAppMediaType.DOCUMENT]: {
    label: 'Add Document',
    tooltip: 'Selecciona un documento (PDF, DOC, XLS, PPT, TXT, CSV)',
    Icon: PiFile,
  },
  [WhatsAppMediaType.STICKER]: {
    label: 'Add Sticker',
    tooltip: 'Selecciona un sticker (formato WebP)',
    Icon: PiFile,
  },
};

/**
 * Props for the FileInputButton component
 */
interface FileInputButtonProps {
  /** Optional shared menu trigger; keeps file selection and validation in this component. */
  renderTrigger?: (openFilePicker: () => void) => React.ReactNode;
  /** Callback when a file is selected and validated */
  onFileSelect: (file: File, mediaType: WhatsAppMediaType) => void;
  /** Callback when file validation fails */
  onValidationError?: (error: string) => void;
  /** Media types to accept (defaults to all WhatsApp supported types) */
  acceptedTypes?: WhatsAppMediaType[] | 'all';
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Whether the button is in loading state */
  loading?: boolean;
  /** Component size variant */
  size?: ComponentSize;
  /** Component variant for styling */
  variant?: ComponentVariant;
  /** Component appearance */
  appearance?: ComponentAppearance;
  /** Additional CSS classes */
  className?: string;
  /** Custom button text */
  buttonText?: string;
  /** Custom tooltip text */
  tooltipText?: string;
  /** Whether to show icon in button */
  showIcon?: boolean;
  /** Whether to show text in button */
  showText?: boolean;
}

/**
 * FileInputButton component for selecting WhatsApp media files
 */
export const FileInputButton: React.FC<FileInputButtonProps> = ({
  onFileSelect,
  renderTrigger,
  onValidationError,
  acceptedTypes = 'all',
  disabled = false,
  loading = false,
  size = 'md',
  variant = 'primary',
  appearance = 'outline',
  className = '',
  buttonText,
  tooltipText,
  showIcon = true,
  showText = true,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const acceptAttribute = getFileInputAccept(acceptedTypes);

  const singleType =
    acceptedTypes !== 'all' && acceptedTypes.length === 1
      ? acceptedTypes[0]
      : undefined;
  const presentation = singleType ? mediaPresentation[singleType] : undefined;
  const defaultText =
    presentation?.label ??
    (acceptedTypes === 'all' ? 'Attach File' : 'Attach Media');
  const displayText = buttonText?.length
    ? buttonText
    : showText
      ? defaultText
      : '';
  const Icon = showIcon ? presentation?.Icon ?? PiPaperclip : null;
  const defaultTooltip =
    acceptedTypes === 'all'
      ? 'Selecciona un archivo (imágen, video, audio, documento)'
      : presentation?.tooltip ??
        `Selecciona archivo de ${acceptedTypes.map((type) => type.charAt(0).toUpperCase() + type.slice(1)).join(', ')}`;
  const tooltip = tooltipText?.length ? tooltipText : defaultTooltip;

  const handleButtonClick = () => {
    if (!disabled && !loading) {
      fileInputRef.current?.click();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    event.target.value = '';

    if (!file) return;

    const validationResult = validateWhatsAppMediaFile(file);

    if (!validationResult.isValid) {
      const errorMessage = createValidationErrorMessage(validationResult);
      onValidationError?.(errorMessage);
      return;
    }

    if (
      Array.isArray(acceptedTypes) &&
      !acceptedTypes.includes(validationResult.mediaType)
    ) {
      const acceptedTypeNames = acceptedTypes.join(', ');
      const errorMessage = `Selected file is ${validationResult.mediaType} but only ${acceptedTypeNames} files are accepted.`;
      onValidationError?.(errorMessage);
      return;
    }

    onFileSelect(file, validationResult.mediaType);
  };

  const button = (
    <Button
      variant={variant}
      appearance={appearance}
      size={size}
      disabled={disabled}
      loading={loading}
      onClick={handleButtonClick}
      className={className}
      isIconOnly={!displayText}
    >
      <>
        {Icon && <Icon className="w-5 h-5" />}
        {displayText && <span>{displayText}</span>}
      </>
    </Button>
  );

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptAttribute}
        onChange={handleFileChange}
        className="hidden"
        disabled={disabled || loading}
      />

      {renderTrigger ? (
        renderTrigger(handleButtonClick)
      ) : tooltip ? (
        <Tooltip content={tooltip} placement="top" size="sm">
          {button}
        </Tooltip>
      ) : (
        button
      )}
    </>
  );
};
